import { useSyncExternalStore } from 'react';
import { useRouterState } from '@tanstack/react-router';
import { isAdminPath } from '~portal/lib';

/** @desc Which list the platform's nav column shows: the five sections, or the admin tree. */
export type NavMode = 'main' | 'admin';

/**
 * @desc A swap the user made with "Admin →" or "← Back", and the page it was made on. Swapping
 * only changes the list — it never navigates (Tim, 22 Oct 2026); choosing an item in the list is
 * what navigates. The swap holds while the user stays on that page and lapses as soon as the path
 * changes, so after a navigation the column again follows the route.
 */
type Swap = { path: string; mode: NavMode };

let swap: Swap | null = null;
const listeners = new Set<() => void>();

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const snapshot = () => swap;

/** @desc Show `mode` on `path` until the path changes. Shared by the column and the drawer. */
export function swapNav(path: string, mode: NavMode): void {
  swap = { path, mode };
  listeners.forEach((listener) => listener());
}

/** @desc Forget any swap — for tests, which mount a fresh router per case. */
export function resetNavSwap(): void {
  swap = null;
  listeners.forEach((listener) => listener());
}

/**
 * @desc The list to show for `path`: the user's swap while they are still on the page it was made
 * on, else the route's own — the admin tree under `/admin`, the sections everywhere else.
 */
export const navModeFor = (path: string, current: Swap | null): NavMode =>
  current && current.path === path ? current.mode : isAdminPath(path) ? 'admin' : 'main';

/** @desc The nav column's current list, and the swaps to change it without navigating. */
export function useNavMode(): {
  mode: NavMode;
  showAdmin: () => void;
  showMain: () => void;
} {
  const path = useRouterState({ select: (state) => state.location.pathname });
  const current = useSyncExternalStore(subscribe, snapshot, snapshot);
  return {
    mode: navModeFor(path, current),
    showAdmin: () => swapNav(path, 'admin'),
    showMain: () => swapNav(path, 'main'),
  };
}
